import { createFileRoute } from "@tanstack/react-router";
import { getCmsFaqs, type CmsFaqGroup } from "@/lib/cms/website.functions";
import { SiteLayout } from "@/components/SiteLayout";
import { Container } from "@/components/Container";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const TITLE = "Frequently Asked Questions — Poonam Kapur Healthy Kitchen";
const DESC =
  "Answers about delivery areas, order cut-off times, meal plans, pauses and dietary preferences.";

export const Route = createFileRoute("/faq")({
  loader: () => getCmsFaqs(),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: "https://poonamkapur.com/faq" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://poonamkapur.com/faq" }],
  }),
  component: FaqPage,
});

function FaqPage() {
  const groups = Route.useLoaderData() as CmsFaqGroup[];
  return (
    <SiteLayout>
      <Container className="py-16">
        <div className="mx-auto max-w-3xl">
          <h1 className="font-display text-3xl font-semibold tracking-tight md:text-4xl">
            Frequently asked questions
          </h1>
          {groups.length === 0 ? (
            <p className="mt-8 text-sm text-muted-foreground">
              We're preparing our FAQs. Please reach out to our support team in the meantime.
            </p>
          ) : (
            groups.map((group) => (
              <section key={group.id} className="mt-10">
                <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                  {group.name}
                </h2>
                <Accordion type="single" collapsible className="mt-4">
                  {group.faqs.map((faq) => (
                    <AccordionItem key={faq.id} value={faq.id}>
                      <AccordionTrigger className="text-left text-sm font-medium">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent>
                        <div
                          className="prose prose-sm max-w-none dark:prose-invert"
                          dangerouslySetInnerHTML={{ __html: faq.answer_html }}
                        />
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </section>
            ))
          )}
        </div>
      </Container>
    </SiteLayout>
  );
}
